
import { calculateScores, createEmptyNode, createFlattenedItem, hasChildren, hasContent } from '@/app/features/reports/utils/utils';
import { useDeferredValue, useMemo } from 'react';
import { useReportDisplayContext } from '../components/report-viewer-context';
import { ReportGroupModes, ReportHeaderModes } from '../types';
import { FlattenedItem, ReportItem, ReportItemContent } from './entities';
import sortContentBy, { determineSortOrder } from './sorters';
;

/**
 * The single source of truth for report-tile ordering (depth-first, children before a node's
 * own content, each content list re-sorted via `sortContentBy`) — exported so a non-hook
 * consumer (e.g. a print/PDF appendix builder) can call it directly instead of hand-rolling an
 * equivalent traversal that drifts out of sync (raytd/web#142: exactly that happened once,
 * `getSdaAppendixImages.ts`'s `collectOrderedTestItems` visited a node's content before its
 * children, the reverse of this function's real order).
 *
 * `expandedIds` gates recursion, not inclusion: every item is pushed into the result regardless,
 * but a node's own children/content are only visited if `expandedIds.has(item.id)`. For a "fully
 * expanded" traversal (e.g. print output), `expandedIds` must contain the id of every node that
 * `hasChildren`/`hasContent` at every level, not just the top level — a leaf/content id never
 * needs to be included, since a leaf's children/content are unconditionally empty.
 */
function flattenData(
    items: ReportItem[],
    level: number = 0,
    groupMode: ReportGroupModes,
    headerMode: ReportHeaderModes,
    expandedIds: Set<string>,
    isContent = false,
    parents: readonly string[] = [],
    groupHeaderId: string | null = null
): FlattenedItem[] {

    const result: FlattenedItem[] = [];
    // const sortOrder = determineSortOrder(groupMode, headerMode);

    for (const item of items) {
        const isExpanded = expandedIds.has(item.id);
        const scores = calculateScores(item);
        const isFirst = !isContent ? null : items.indexOf(item) === 0;
        const isLast = !isContent ? null : items.indexOf(item) === items.length - 1;
        const currentParents = isContent
            ? parents
            : Object.freeze([...parents, item.title]);

        result.push(createFlattenedItem(
            item,
            scores,
            isExpanded,
            level,
            isContent,
            //@ts-expect-error
            currentParents,
            groupHeaderId,
            isFirst,
            isLast
        ));

        if (isExpanded) {
            if (hasChildren(item)) {
                result.push(...flattenData(
                    item.children!,
                    level + 1,
                    groupMode,
                    headerMode,
                    expandedIds,
                    false,
                    currentParents
                ));
            }

            if (hasContent(item)) {
                const sortedContent = sortContentBy(item.content as ReportItemContent[], groupMode);
                const groupHeaderId = item.id;

                result.push(...flattenData(
                    //@ts-expect-error
                    sortedContent as unknown as ReportItemContent[],
                    level + 1,
                    groupMode,
                    headerMode,
                    expandedIds,
                    true,
                    currentParents,
                    groupHeaderId,

                ));
            }

            if (!hasContent(item) && !hasChildren(item)) {
                result.push(createEmptyNode(item.id, level + 1));
            }
        }
    }

    return result;


}

/**
 * Recursively collects header IDs from a list of ReportItems.
 * A header is any item with content or children.
 * @param data - Array of ReportItem objects
 * @returns Array of header IDs as strings
 */
function extractHeaderIds(data: ReportItem[]): string[] {
    if (!Array.isArray(data) || data.length === 0) return []

    const headerIds = new Set<string>()

    for (const item of data) {
        if (hasChildren(item)) headerIds.add(item.id)
        if (Array.isArray(item.children) && item.children.length > 0) {
            for (const childId of extractHeaderIds(item.children)) headerIds.add(childId)
        }
    }

    return Array.from(headerIds)
}

function useHeaderItems(data: ReportItem[], groupMode: ReportGroupModes) {
    const { headerMode } = useReportDisplayContext();

    const headerItems = useMemo(() => {
        // return flattenData(data || [], 0, groupMode, headerMode, expandedIds);
        // data.filter(item => hasContent(item));
        const headerIds = new Set(extractHeaderIds(data));
        return flattenData(data, 0, groupMode, headerMode, headerIds);
        // return data;
    }, [groupMode, headerMode, data]);

    return useDeferredValue(headerItems);
}

/**
 * Drops the interim header rows of a single-item chain (raytd/web#232, QA 460.01).
 *
 * A test that sits under several nested groups used to render one header row per level, each
 * truncating its ancestors differently, so the rows read as unrelated. An expanded header whose
 * only child is another group and which holds no tests of its own adds no information: the
 * ultimate header already carries the full path in `parents`. Collapsed headers are always kept
 * (they are the only way to expand), as is any header with more than one child or its own tests.
 *
 * Interactive list only: print/PDF output shares `flattenData` and must keep every header.
 */
function collapseSingleItemChains(items: FlattenedItem[]): FlattenedItem[] {
    // Only an expanded header with exactly one child group and no tests of its own is dropped.
    // Test rows and empty-node rows never match: neither is ever in `expandedIds` (so they are not
    // `isExpanded`), and an empty node has `length` 0.
    return items.filter(item => !item.isExpanded || item.hasContent || item.length !== 1);
}

interface FlattenedItemsOptions {
    /** Hide interim headers of single-item chains. Off by default so print/PDF stay unchanged. */
    collapseSingleItemChains?: boolean;
}

function useFlattenedItems(
    data: ReportItem[],
    expandedIds: Set<string>,
    groupMode: ReportGroupModes,
    options: FlattenedItemsOptions = {}
) {

    const { headerMode } = useReportDisplayContext();
    const collapseChains = Boolean(options.collapseSingleItemChains);

    const flattenedData = useMemo(() => {

        // get all the header ids' from data.
        // const headerIds = collectAllGroups(data || [], new Set<string>(), false);

        const rows = flattenData(data || [], 0, groupMode, headerMode, expandedIds);
        return collapseChains ? collapseSingleItemChains(rows) : rows;
    }, [expandedIds, groupMode, headerMode, data, collapseChains]);

    return useDeferredValue(flattenedData);

}

export {useFlattenedItems , useHeaderItems, flattenData, collapseSingleItemChains};